import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState, useCallback } from "react";
import { Users, MessageSquare, Send, ArrowLeft, Check, X, UserPlus, User } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const timeAgo = (iso) => {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString();
};

const Messaging = forwardRef((props, ref) => {
  const { user } = useAuth();
  const [friendsOpen, setFriendsOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [thread, setThread] = useState(null); // username
  const [threadMsgs, setThreadMsgs] = useState([]);
  const [convos, setConvos] = useState([]);
  const [requests, setRequests] = useState([]);
  const [friends, setFriends] = useState([]);
  const [unread, setUnread] = useState(0);
  const [text, setText] = useState("");
  const [addName, setAddName] = useState("");
  const [note, setNote] = useState("");
  const scrollRef = useRef(null);

  const refreshBadges = useCallback(async () => {
    if (!user) return;
    try {
      const [u, r] = await Promise.all([
        api.get("/messages/unread_count"),
        api.get("/friends/requests"),
      ]);
      setUnread(u.data.unread);
      setRequests(r.data);
    } catch (e) {}
  }, [user]);

  useEffect(() => {
    refreshBadges();
    if (!user) return;
    const t = setInterval(refreshBadges, 15000);
    return () => clearInterval(t);
  }, [user, refreshBadges]);

  const loadConvos = async () => {
    const { data } = await api.get("/messages/conversations");
    setConvos(data);
  };

  const openThread = useCallback(async (username) => {
    setMsgOpen(true);
    setThread(username);
    const { data } = await api.get(`/messages/${encodeURIComponent(username)}`);
    setThreadMsgs(data);
    refreshBadges();
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
  }, [refreshBadges]);

  const loadFriends = async () => {
    const [f, r] = await Promise.all([api.get("/friends"), api.get("/friends/requests")]);
    setFriends(f.data);
    setRequests(r.data);
  };

  const addFriend = useCallback(async (username) => {
    try {
      await api.post("/friends/request", { username });
      setNote(`Friend request sent to ${username}.`);
    } catch (err) {
      setNote(err?.response?.data?.detail || "Could not send request.");
    }
  }, []);

  useImperativeHandle(ref, () => ({
    openThread,
    addFriend,
    openFriends: () => { setFriendsOpen(true); loadFriends(); },
    openMessages: () => { setMsgOpen(true); setThread(null); loadConvos(); },
  }));

  const respond = async (request_id, action) => {
    await api.post("/friends/respond", { request_id, action });
    loadFriends();
    refreshBadges();
  };

  const sendMsg = async () => {
    if (!text.trim()) return;
    const { data } = await api.post(`/messages/${encodeURIComponent(thread)}`, { body: text });
    setThreadMsgs((m) => [...m, data]);
    setText("");
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
  };

  // auto-poll open thread
  useEffect(() => {
    if (!thread || !msgOpen) return;
    const t = setInterval(async () => {
      const { data } = await api.get(`/messages/${encodeURIComponent(thread)}`);
      setThreadMsgs(data);
    }, 6000);
    return () => clearInterval(t);
  }, [thread, msgOpen]);

  if (!user) return null;

  const Badge = ({ n }) =>
    n > 0 ? (
      <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#e0574c] px-1 text-xs font-600 text-white">
        {n}
      </span>
    ) : null;

  return (
    <>
      <div className="mb-4 flex gap-2">
        <button
          onClick={() => { setFriendsOpen(true); loadFriends(); }}
          className="inline-flex items-center gap-2 rounded border border-white/15 bg-[#242424] px-4 py-2 font-display text-sm uppercase tracking-wide text-[#e0a82e] transition-colors hover:border-[#e0a82e]"
        >
          <Users className="h-4 w-4" /> Friends <Badge n={requests.length} />
        </button>
        <button
          onClick={() => { setMsgOpen(true); setThread(null); loadConvos(); }}
          className="inline-flex items-center gap-2 rounded border border-white/15 bg-[#242424] px-4 py-2 font-display text-sm uppercase tracking-wide text-[#e0a82e] transition-colors hover:border-[#e0a82e]"
        >
          <MessageSquare className="h-4 w-4" /> Messages <Badge n={unread} />
        </button>
      </div>

      {/* FRIENDS DIALOG */}
      <Dialog open={friendsOpen} onOpenChange={setFriendsOpen}>
        <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto border-white/10 bg-[#1f1f1f] text-[#e9e9e9]">
          <DialogHeader><DialogTitle className="font-display text-xl uppercase tracking-wide text-[#e0a82e]">Friends</DialogTitle></DialogHeader>

          <div className="flex gap-2">
            <input
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              placeholder="Add a friend by username"
              className="flex-1 rounded border border-white/15 bg-[#1e1e1e] px-3 py-2 text-sm text-[#eee] outline-none focus:border-[#e0a82e]"
            />
            <button
              onClick={async () => { if (addName.trim()) { await addFriend(addName.trim()); setAddName(""); } }}
              className="rounded bg-[#e0a82e] px-3 py-2 text-[#1c1c1c] hover:bg-[#f2b93f]"
            >
              <UserPlus className="h-4 w-4" />
            </button>
          </div>
          {note && <p className="text-xs text-[#e0a82e]">{note}</p>}

          {requests.length > 0 && (
            <div>
              <p className="font-display text-xs uppercase tracking-widest text-[#e0574c]">Requests</p>
              <div className="mt-2 space-y-2">
                {requests.map((r) => (
                  <div key={r.request_id} className="flex items-center justify-between rounded bg-[#262626] px-3 py-2">
                    <span className="text-sm text-[#e9e9e9]">{r.from}</span>
                    <div className="flex gap-2">
                      <button onClick={() => respond(r.request_id, "accept")} className="rounded bg-[#e0a82e] p-1.5 text-[#1c1c1c] hover:bg-[#f2b93f]"><Check className="h-4 w-4" /></button>
                      <button onClick={() => respond(r.request_id, "decline")} className="rounded bg-[#333] p-1.5 text-[#c0c0c0] hover:text-[#e0574c]"><X className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="font-display text-xs uppercase tracking-widest text-[#8f8f8f]">Your Friends ({friends.length})</p>
            <div className="mt-2 space-y-2">
              {friends.length === 0 && <p className="text-sm text-[#9a9a9a]">No friends yet. Add someone by username or from their post.</p>}
              {friends.map((f) => (
                <div key={f} className="flex items-center justify-between rounded bg-[#262626] px-3 py-2">
                  <span className="flex items-center gap-2 text-sm text-[#e9e9e9]"><User className="h-4 w-4 text-[#e0a82e]" /> {f}</span>
                  <button onClick={() => { setFriendsOpen(false); openThread(f); }} className="inline-flex items-center gap-1 rounded border border-[#e0a82e]/50 px-3 py-1 text-xs uppercase text-[#e0a82e] hover:bg-[#e0a82e] hover:text-[#1c1c1c]">
                    <MessageSquare className="h-3.5 w-3.5" /> Message
                  </button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* MESSAGES DIALOG */}
      <Dialog open={msgOpen} onOpenChange={(o) => { setMsgOpen(o); if (!o) setThread(null); }}>
        <DialogContent className="flex h-[80vh] max-w-md flex-col overflow-hidden border-white/10 bg-[#1f1f1f] p-0 text-[#e9e9e9]">
          {!thread ? (
            <>
              <DialogHeader className="border-b border-white/10 p-4"><DialogTitle className="font-display text-xl uppercase tracking-wide text-[#e0a82e]">Messages</DialogTitle></DialogHeader>
              <div className="flex-1 overflow-y-auto p-2">
                {convos.length === 0 && <p className="p-6 text-center text-sm text-[#9a9a9a]">No conversations yet. Message a friend to start chatting.</p>}
                {convos.map((c) => (
                  <button key={c.username} onClick={() => openThread(c.username)} className="flex w-full items-center gap-3 rounded p-3 text-left hover:bg-[#2a2a2a]">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#333] text-[#e0a82e]"><User className="h-5 w-5" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-500 text-[#f0f0f0]">{c.username}</span>
                        <span className="text-xs text-[#8f8f8f]">{timeAgo(c.lastAt)}</span>
                      </div>
                      <p className="truncate text-sm text-[#a8a8a8]">{c.lastMessage}</p>
                    </div>
                    {c.unread > 0 && <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#e0574c] px-1 text-xs text-white">{c.unread}</span>}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <DialogHeader className="border-b border-white/10 p-4">
                <DialogTitle className="flex items-center gap-2 font-display text-lg uppercase tracking-wide text-[#e0a82e]">
                  <button onClick={() => { setThread(null); loadConvos(); }} className="text-[#c0c0c0] hover:text-[#e0a82e]"><ArrowLeft className="h-5 w-5" /></button>
                  {thread}
                </DialogTitle>
              </DialogHeader>
              <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto p-4">
                {threadMsgs.length === 0 && <p className="text-center text-sm text-[#9a9a9a]">Say hello 👋</p>}
                {threadMsgs.map((m) => (
                  <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.mine ? "bg-[#e0a82e] text-[#1c1c1c]" : "bg-[#2f2f2f] text-[#e9e9e9]"}`}>
                      <p className="whitespace-pre-wrap break-words">{m.body}</p>
                      <span className={`mt-1 block text-[10px] ${m.mine ? "text-[#5a4a1a]" : "text-[#8f8f8f]"}`}>{timeAgo(m.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 border-t border-white/10 p-3">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendMsg()}
                  placeholder="Type a message..."
                  className="flex-1 rounded-full border border-white/15 bg-[#1e1e1e] px-4 py-2 text-sm text-[#eee] outline-none focus:border-[#e0a82e]"
                />
                <button onClick={sendMsg} className="rounded-full bg-[#e0a82e] p-2.5 text-[#1c1c1c] hover:bg-[#f2b93f]"><Send className="h-4 w-4" /></button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
});

Messaging.displayName = "Messaging";
export default Messaging;
